// ---------------------------------------------------------------------------
// The CRM settings from the browser, over REST (R11b)
//
// Settings → CRM, the campaign wizard and the Add-Leads dialog asked BaseQL
// over the socket (`getCrmConfig`, `listCrmConnections`, `disconnectCrmConnection`
// and the three HubSpot Lead Status mapping operations). They call these routes
// now (entrypoints/sdr/src/server/routes/crm-settings.ts), all behind the login:
//
//   GET    /api/crm/config                                       → {ok, hubspot}
//   GET    /api/crm/connections                                  → {ok, connections}
//   DELETE /api/crm/connections/:id                              → {ok}; 404 when not yours
//   GET    /api/crm/connections/:id/lead-status-mapping          → {ok, mapping}
//   POST   /api/crm/connections/:id/lead-status-mapping/suggest  → {ok, mapping}
//   PUT    /api/crm/connections/:id/lead-status-mapping {mapping} → {ok}
//
// A refusal throws with the server's own words — the ones the page showed
// when they came over the socket ("HubSpot connection not found", "None of the
// chosen values match …"). With no words of the server's (a dropped connection,
// a page that is not JSON) it throws with the words the page falls back on.
//
// The shapes below are the wire format; the server builds them field by field
// and never sends a token.
// ---------------------------------------------------------------------------

/** Which CRM providers the server can offer, and how. */
export interface CrmConfig {
  hubspot: {
    enabled: boolean;
    /** 'private_app' | 'oauth' | 'disabled' */
    mode: string;
    oauthAvailable: boolean;
  };
}

/** One CRM connection as the Settings page lists it — metadata only, never a token. */
export interface CrmConnectionView {
  _id: string;
  provider: string;
  accountId?: string;
  accountLabel?: string;
  authMode?: string;
  /** The deployment-wide connection from the server's environment. */
  isSystem: boolean;
  status: string;
  scopes?: string;
  connectedAt: string;
  lastUsedAt?: string;
  lastError?: string;
}

export interface HubspotStatusOptionView {
  value: string;
  label: string;
}

export interface HubspotLeadStatusRowView {
  /** 'contacted' | 'replied' | 'bounced' | 'opted_out' */
  localStatus: string;
  localLabel: string;
  /** The confirmed value ('' = not set). */
  savedValue: string;
  /** A value to prefill when nothing is saved yet ('' = no suggestion). */
  suggestedValue: string;
}

/** The portal's Lead Status options and one row per local state. */
export interface HubspotLeadStatusMappingView {
  connectionId: string;
  /** Could the portal's hs_lead_status options be read? (false → scope / connection issue) */
  optionsReadable: boolean;
  /** When not readable, the error text to show. */
  readError?: string;
  /** Whether the suggested values came from AI (true) or the deterministic resolver (false). */
  aiSuggested: boolean;
  hasSaved: boolean;
  options: HubspotStatusOptionView[];
  rows: HubspotLeadStatusRowView[];
}

export const CRM_CONFIG_URL = '/api/crm/config';
export const CRM_CONNECTIONS_URL = '/api/crm/connections';

export const connectionUrl = (connectionId: string) => `${CRM_CONNECTIONS_URL}/${encodeURIComponent(connectionId)}`;
export const mappingUrl = (connectionId: string) => `${connectionUrl(connectionId)}/lead-status-mapping`;

/** The words the page falls back on when the server gives none. */
export const CRM_FAILED = {
  config: 'Could not read the CRM configuration',
  connections: 'Could not load the CRM connections',
  disconnect: 'Failed to disconnect CRM',
  load: 'Failed to load the mapping',
  suggest: 'Failed to suggest a mapping',
  save: 'Failed to save the mapping',
} as const;

/** The body of a JSON answer, or null when there is none. */
async function bodyOf(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await res.json();
    return body && typeof body === 'object' && !Array.isArray(body) ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** One request: the answer's body when the server said ok, else an Error with its words or `fallback`. */
async function call(
  url: string,
  init: RequestInit,
  fallback: string,
): Promise<{ status: number; body: Record<string, unknown> }> {
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers: { accept: 'application/json', ...(init.headers ?? {}) } });
  } catch {
    throw new Error(fallback);
  }
  const body = await bodyOf(res);
  if (res.ok && body?.ok === true) return { status: res.status, body };
  const reason = typeof body?.error === 'string' && body.error ? body.error : fallback;
  throw Object.assign(new Error(reason), { status: res.status });
}

/** Which CRM providers the server offers. */
export async function getCrmConfig(): Promise<CrmConfig> {
  const { body } = await call(CRM_CONFIG_URL, { method: 'GET' }, CRM_FAILED.config);
  const hubspot = (body.hubspot ?? {}) as Partial<CrmConfig['hubspot']>;
  return {
    hubspot: {
      enabled: hubspot.enabled === true,
      mode: typeof hubspot.mode === 'string' ? hubspot.mode : 'disabled',
      oauthAvailable: hubspot.oauthAvailable === true,
    },
  };
}

/** Your CRM connections and the deployment-wide one. */
export async function listCrmConnections(): Promise<CrmConnectionView[]> {
  const { body } = await call(CRM_CONNECTIONS_URL, { method: 'GET' }, CRM_FAILED.connections);
  return Array.isArray(body.connections) ? (body.connections as CrmConnectionView[]) : [];
}

/** Disconnect one of your connections. False when there is none of yours with this id. */
export async function disconnectCrmConnection(connectionId: string): Promise<boolean> {
  try {
    await call(connectionUrl(connectionId), { method: 'DELETE' }, CRM_FAILED.disconnect);
    return true;
  } catch (err) {
    if ((err as { status?: number }).status === 404) return false;
    throw err;
  }
}

/** The portal's options and the saved (or best-guess) mapping. */
export async function getHubspotLeadStatusMapping(connectionId: string): Promise<HubspotLeadStatusMappingView> {
  const { body } = await call(mappingUrl(connectionId), { method: 'GET' }, CRM_FAILED.load);
  return body.mapping as HubspotLeadStatusMappingView;
}

/** The same view with the values an AI proposes. Saves nothing. */
export async function suggestHubspotLeadStatusMapping(connectionId: string): Promise<HubspotLeadStatusMappingView> {
  const { body } = await call(`${mappingUrl(connectionId)}/suggest`, { method: 'POST' }, CRM_FAILED.suggest);
  return body.mapping as HubspotLeadStatusMappingView;
}

/** Save the mapping: local state → the portal's option value ('' = don't set). All empty clears it. */
export async function saveHubspotLeadStatusMapping(
  connectionId: string,
  mapping: Record<string, string>,
): Promise<void> {
  await call(
    mappingUrl(connectionId),
    { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ mapping }) },
    CRM_FAILED.save,
  );
}
